import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const DEFAULT_CENTER = { lat: 33.5138, lng: 36.2765 };

const iconUrl = new URL("leaflet/dist/images/marker-icon.png", import.meta.url).toString();
const iconRetinaUrl = new URL("leaflet/dist/images/marker-icon-2x.png", import.meta.url).toString();
const shadowUrl = new URL("leaflet/dist/images/marker-shadow.png", import.meta.url).toString();

L.Icon.Default.mergeOptions({
  iconUrl,
  iconRetinaUrl,
  shadowUrl,
});

type MapPickerProps = {
  latitude: string;
  longitude: string;
  onChange: (latitude: string, longitude: string) => void;
};

const formatCoordinate = (value: number) => value.toFixed(6);

const parseCoordinate = (value: string, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

function MapViewUpdater({ latitude, longitude }: { latitude: number; longitude: number }) {
  const map = useMap();

  useEffect(() => {
    map.setView([latitude, longitude], map.getZoom(), { animate: false });
  }, [latitude, longitude, map]);

  return null;
}

function MapClickHandler({ onPick }: { onPick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(event) {
      onPick(event.latlng.lat, event.latlng.lng);
    },
  });

  return null;
}

type MapCanvasProps = {
  latitude: number;
  longitude: number;
  interactive?: boolean;
  onPick?: (lat: number, lng: number) => void;
};

function MapCanvas({ latitude, longitude, interactive = true, onPick }: MapCanvasProps) {
  return (
    <MapContainer
      center={[latitude, longitude]}
      zoom={13}
      className="h-full w-full"
      scrollWheelZoom={interactive}
      dragging={interactive}
      doubleClickZoom={interactive}
      zoomControl={interactive}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <MapViewUpdater latitude={latitude} longitude={longitude} />
      {interactive && onPick && <MapClickHandler onPick={onPick} />}
      <Marker
        position={[latitude, longitude]}
        draggable={interactive}
        eventHandlers={
          interactive && onPick
            ? {
                dragend(event) {
                  const marker = event.target as L.Marker;
                  const { lat, lng } = marker.getLatLng();
                  onPick(lat, lng);
                },
              }
            : undefined
        }
      />
    </MapContainer>
  );
}

export default function MapPicker({ latitude, longitude, onChange }: MapPickerProps) {
  const position = useMemo(() => {
    return {
      lat: parseCoordinate(latitude, DEFAULT_CENTER.lat),
      lng: parseCoordinate(longitude, DEFAULT_CENTER.lng),
    };
  }, [latitude, longitude]);

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<
    Array<{ display_name: string; lat: string; lon: string }>
  >([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const searchBoxRef = useRef<HTMLDivElement | null>(null);
  const searchResultsRef = useRef<HTMLDivElement | null>(null);

  const handlePick = (lat: number, lng: number) => {
    onChange(formatCoordinate(lat), formatCoordinate(lng));
  };

  const searchLocation = async () => {
    const query = searchQuery.trim();
    if (!query) {
      setSearchError("يرجى كتابة اسم المكان أولاً.");
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          query
        )}&limit=5`
      );
      if (!response.ok) {
        setSearchError("تعذر البحث حالياً.");
        setSearchResults([]);
        return;
      }
      const results = (await response.json()) as Array<{
        display_name: string;
        lat: string;
        lon: string;
      }>;
      setSearchResults(results);
      if (results.length === 0) {
        setSearchError("لا توجد نتائج مطابقة.");
      }
    } catch {
      setSearchError("تعذر البحث حالياً.");
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    if (searchResults.length === 0) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (searchResultsRef.current?.contains(target)) return;
      if (searchBoxRef.current?.contains(target)) return;
      setSearchResults([]);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchResults.length]);

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setSearchError("المتصفح لا يدعم تحديد الموقع.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (positionData) => {
        setSearchError(null);
        handlePick(positionData.coords.latitude, positionData.coords.longitude);
      },
      () => {
        setSearchError("تعذر الحصول على موقعك الحالي.");
      }
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <span>افتح الخريطة الكبيرة لتحديد الموقع بدقة.</span>
        <Button type="button" variant="outline" onClick={() => setIsOpen(true)}>
          فتح الخريطة
        </Button>
      </div>

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div
            className="flex h-[85vh] w-full max-w-5xl flex-col gap-4 rounded-xl bg-background p-4 shadow-xl"
            role="dialog"
            aria-modal="true"
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                setIsOpen(false);
              }
            }}
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold">تحديد الموقع</p>
                <p className="text-xs text-muted-foreground">
                  ابحث عن المكان أو اضغط على الخريطة لوضع المؤشر.
                </p>
              </div>
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                إغلاق
              </Button>
            </div>

            <div ref={searchBoxRef} className="flex flex-wrap items-center gap-2">
              <Input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="ابحث عن مدينة، حي، أو معلم..."
                className="flex-1"
              />
              <Button
                type="button"
                variant="secondary"
                onClick={searchLocation}
                disabled={isSearching}
              >
                {isSearching ? "جاري البحث..." : "بحث"}
              </Button>
              <Button type="button" variant="outline" onClick={useCurrentLocation}>
                موقعي الحالي
              </Button>
            </div>

            {searchError && <p className="text-sm text-destructive">{searchError}</p>}
            {searchResults.length > 0 && (
              <div
                ref={searchResultsRef}
                className="max-h-32 overflow-auto rounded-md border bg-muted/30 p-2 text-sm"
              >
                {searchResults.map((result) => (
                  <button
                    key={`${result.lat}-${result.lon}`}
                    type="button"
                    className="block w-full rounded-md px-2 py-1 text-start hover:bg-muted"
                    onClick={() => {
                      handlePick(Number(result.lat), Number(result.lon));
                      setSearchQuery(result.display_name);
                    }}
                  >
                    {result.display_name}
                  </button>
                ))}
              </div>
            )}

            <div className="min-h-0 flex-1 overflow-hidden rounded-lg border">
              <MapCanvas latitude={position.lat} longitude={position.lng} onPick={handlePick} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
